import { hash } from "daedalus-core";
import { portraitClipFor, portraitGap, type PortraitClips, type PortraitTimer } from "odyssey-shared";

/**
 * Plays a speaker's dialogue portrait (P2.22-T37): the blink timer and the actions timer of a clip table
 * (`odyssey-shared/src/sprite/portrait-clips.ts`) run on their own gaps, each firing plays its clip by showing one cell
 * after another for the holds the clip gives, and the face returns to the rest cell between clips. A timer that fires
 * while a clip runs is queued and plays when the clip ends. Gaps and clips come from the shared seeded hash, so a seed
 * plays one portrait everywhere. The clock is injected so a test drives the timers by hand.
 */

/**
 * The part of the timer API the player uses.
 */
export interface PortraitClock {
  setTimeout(run: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

/**
 * A running portrait, stopped when its window closes.
 */
export interface PortraitPlayer {
  /** Clears every timer; no cell is drawn after it. */
  stop(): void;
}

const TIMERS: readonly PortraitTimer[] = ["blink", "actions"];

/**
 * A seed for a portrait from its strip's URL and the count of windows opened so far, so one speaker plays a given
 * sequence on every machine for the same count while each conversation plays a different one.
 *
 * @param text The URL.
 * @param opened How many portraits the page has opened, from 0.
 * @returns An unsigned 32-bit integer.
 */
export function portraitSeed(text: string, opened: number): number {
  let value = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    value = Math.imul(value ^ text.charCodeAt(index), 0x01000193) >>> 0;
  }
  return hash(value, opened >>> 0);
}

/**
 * Starts a portrait: shows the rest cell and arms both timers.
 *
 * @param table The clip table.
 * @param seed The seed, an unsigned 32-bit integer (`portraitSeed`).
 * @param clock The timer API.
 * @param show Draws a cell by its index in the strip.
 * @returns The player, whose `stop` ends every timer.
 */
export function startPortrait(table: PortraitClips, seed: number, clock: PortraitClock, show: (cell: number) => void): PortraitPlayer {
  const armed = new Map<PortraitTimer, unknown>();
  const fired: Record<PortraitTimer, number> = { blink: 0, actions: 0 };
  const played: Record<PortraitTimer, number> = { blink: 0, actions: 0 };
  const waiting: PortraitTimer[] = [];
  let running: unknown = null;
  let playing = false;
  let stopped = false;
  let lastAction: string | null = null;

  /**
   * Arms a timer for its next gap.
   *
   * @param timer Which timer.
   */
  const arm = (timer: PortraitTimer): void => {
    const wait = portraitGap(table, timer, seed, fired[timer]);
    fired[timer] += 1;
    armed.set(timer, clock.setTimeout(() => due(timer), wait));
  };

  /**
   * Plays the oldest queued timer's clip if none is running.
   */
  const next = (): void => {
    const timer = waiting.shift();
    if (timer === undefined || stopped) {
      return;
    }
    const name = portraitClipFor(table, timer, seed, played[timer], timer === "actions" ? lastAction : null);
    played[timer] += 1;
    if (timer === "actions") {
      lastAction = name;
    }
    const clip = table.clips[name];
    if (clip === undefined) {
      return;
    }
    playing = true;
    const step = (at: number): void => {
      if (at >= clip.frames.length) {
        playing = false;
        running = null;
        show(table.rest);
        next();
        return;
      }
      show(clip.frames[at] as number);
      running = clock.setTimeout(() => step(at + 1), clip.ms[at] as number);
    };
    step(0);
  };

  /**
   * A timer has come due: it waits for a running clip, then plays; its next gap starts at once.
   *
   * @param timer Which timer.
   */
  const due = (timer: PortraitTimer): void => {
    if (stopped) {
      return;
    }
    arm(timer);
    if (!waiting.includes(timer)) {
      waiting.push(timer);
    }
    if (!playing) {
      next();
    }
  };

  show(table.rest);
  for (const timer of TIMERS) {
    arm(timer);
  }
  return {
    stop(): void {
      stopped = true;
      for (const handle of armed.values()) {
        clock.clearTimeout(handle);
      }
      if (running !== null) {
        clock.clearTimeout(running);
      }
      armed.clear();
      running = null;
      playing = false;
      waiting.length = 0;
    },
  };
}

/**
 * What the portrait's run reads from the page: the timers, whether motion is reduced now, and a way to hear it change.
 */
export interface PortraitEnvironment {
  readonly clock: PortraitClock;
  reduced(): boolean;
  /** Calls `onChange` whenever reduced motion may have changed; returns the call that stops listening. */
  watch(onChange: () => void): () => void;
}

/**
 * Runs a portrait for as long as its window is open: the rest cell alone while motion is reduced, the clips otherwise,
 * switching as reduced motion changes. Nothing runs after the returned call.
 *
 * @param table The clip table.
 * @param seed The seed (`portraitSeed`).
 * @param environment The page's timers and reduced motion.
 * @param show Draws a cell by its index in the strip.
 * @returns The call that ends the run: it stops the player and the listener.
 */
export function runPortrait(table: PortraitClips, seed: number, environment: PortraitEnvironment, show: (cell: number) => void): () => void {
  let player: PortraitPlayer | null = null;
  /**
   * Shows the rest cell and starts or stops the clips as motion is reduced or not.
   */
  const apply = (): void => {
    player?.stop();
    player = null;
    if (environment.reduced()) {
      show(table.rest);
    } else {
      player = startPortrait(table, seed, environment.clock, show);
    }
  };
  apply();
  const unwatch = environment.watch(apply);
  return () => {
    unwatch();
    player?.stop();
    player = null;
  };
}
